import { v4 as uuid } from "uuid";
import { AssetType, HeaderPositionType, TextStyleType } from "../constants";
import { BaseCategory } from "./BaseCategory";
import { SlideCategories } from "./CategoryController";
import { SlideModelBuilder } from "./SlideModelBuilder";

export class PhotosWithTextCategory extends BaseCategory {
    get name() {
        return "Photo";
    }

    get type() {
        return SlideCategories.PHOTOS_WITH_TEXT;
    }

    get icon() {
        return null;
    }

    get description() {
        return "Use for multiple photos with labels, attributions or descriptions.";
    }

    getPrompt() {
        return `Create a list of items with photos and text based on the user's prompt.
        Each item should have a title, description, and an image search term.
        Provide between 2-5 items.`;
    }

    getImageDescription() {
        return `If the userinput already describes a specific image, just rewrite that description.
If the userinput does not suggest a specific image, describe an image that complements the userinput.

Only describe the subject and contents of the image. Do not include instructions for style, color, or composition unless specifically relevant to the content.
Make sure the prompt is clear and descriptive but not long or overly detailed (ie. "close up of the moon", "two business people in a meeting", "abstract representation of time").`;
    }

    getPromptSchema(options) {
        if (options.preserveExactText) {
            return {
                items: [{
                    title: "the exact primary text provided for the item",
                    description: "the exact secondary text provided for the item or leave empty if none",
                    imageDescription: this.getImageDescription()
                }]
            };
        } else {
            return {
                items: [{
                    title: "either a short title or a bullet point",
                    subbullets: [{ text: "string" }],
                    description: "an optional short description or explanation for the item",
                    imageDescription: this.getImageDescription(),
                }]
            };
        }
    }

    validateGeneratedModel(model) {
        if (!this.validateArray(model.items)) {
            return false;
        }
        return true;
    }

    getSlideModels(model, options = {}) {
        let models = [];

        models.push(this.buildPhotoTextListSlide(model));

        // const imageCount = Math.min(Math.max(model.items?.length || 6, 1), 10);
        //
        // // Get different layout variations for this image count
        // const layouts = this.getLayoutsForImageCount(imageCount);
        //
        // // Create a slide for each layout variation
        // for (let i = 0; i < Math.min(layouts.length, 4); i++) {
        //     const layout = layouts[i];
        //     models.push(this.buildPhotoCollageSlide(model, imageCount, layout));
        // }
        //
        // // Add a preserve aspect ratio variation
        // models.push(this.buildPhotoCollageSlide(model, imageCount, {
        //     layoutEngineType: "preserve-aspect",
        //     gridLayout: layouts[0].gridLayout,
        //     fullBleed: false,
        //     showGutter: true
        // }));

        return models;
    }

    getLayoutsForImageCount(count) {
        const layouts = {
            1: [
                { gridLayout: { cols: 12, rows: 12, cells: [{ x: 0, y: 0, width: 12, height: 12 }] }, fullBleed: false, showGutter: false, layoutEngineType: "flex" },
                { gridLayout: { cols: 12, rows: 12, cells: [{ x: 0, y: 0, width: 12, height: 12 }] }, fullBleed: true, showGutter: false, layoutEngineType: "flex" }
            ],
            2: [
                { gridLayout: { cols: 12, rows: 12, cells: [{ x: 0, y: 0, width: 6, height: 12 }, { x: 6, y: 0, width: 6, height: 12 }] }, fullBleed: false, showGutter: true, layoutEngineType: "flex" },
                { gridLayout: { cols: 12, rows: 12, cells: [{ x: 0, y: 0, width: 12, height: 6 }, { x: 0, y: 6, width: 12, height: 6 }] }, fullBleed: false, showGutter: true, layoutEngineType: "flex" },
                { gridLayout: { cols: 12, rows: 12, cells: [{ x: 0, y: 0, width: 8, height: 12 }, { x: 8, y: 0, width: 4, height: 12 }] }, fullBleed: true, showGutter: false, layoutEngineType: "flex" }
            ],
            3: [
                { gridLayout: { cols: 12, rows: 12, cells: [{ x: 0, y: 0, width: 6, height: 12 }, { x: 6, y: 0, width: 6, height: 6 }, { x: 6, y: 6, width: 6, height: 6 }] }, fullBleed: false, showGutter: true, layoutEngineType: "flex" },
                { gridLayout: { cols: 12, rows: 12, cells: [{ x: 0, y: 0, width: 4, height: 12 }, { x: 4, y: 0, width: 4, height: 12 }, { x: 8, y: 0, width: 4, height: 12 }] }, fullBleed: false, showGutter: true, layoutEngineType: "flex" },
                { gridLayout: { cols: 12, rows: 12, cells: [{ x: 0, y: 0, width: 12, height: 6 }, { x: 0, y: 6, width: 6, height: 6 }, { x: 6, y: 6, width: 6, height: 6 }] }, fullBleed: false, showGutter: true, layoutEngineType: "flex" }
            ],
            4: [
                { gridLayout: { cols: 12, rows: 12, cells: [{ x: 0, y: 0, width: 6, height: 6 }, { x: 6, y: 0, width: 6, height: 6 }, { x: 0, y: 6, width: 6, height: 6 }, { x: 6, y: 6, width: 6, height: 6 }] }, fullBleed: false, showGutter: true, layoutEngineType: "flex" },
                { gridLayout: { cols: 12, rows: 12, cells: [{ x: 0, y: 0, width: 8, height: 6 }, { x: 8, y: 0, width: 4, height: 6 }, { x: 0, y: 6, width: 4, height: 6 }, { x: 4, y: 6, width: 8, height: 6 }] }, fullBleed: false, showGutter: true, layoutEngineType: "flex" },
                { gridLayout: { cols: 12, rows: 12, cells: [{ x: 0, y: 0, width: 12, height: 6 }, { x: 0, y: 6, width: 4, height: 6 }, { x: 4, y: 6, width: 4, height: 6 }, { x: 8, y: 6, width: 4, height: 6 }] }, fullBleed: true, showGutter: false, layoutEngineType: "flex" }
            ],
            5: [
                { gridLayout: { cols: 12, rows: 12, cells: [{ x: 0, y: 0, width: 4, height: 12 }, { x: 4, y: 0, width: 4, height: 6 }, { x: 8, y: 0, width: 4, height: 6 }, { x: 4, y: 6, width: 4, height: 6 }, { x: 8, y: 6, width: 4, height: 6 }] }, fullBleed: false, showGutter: true, layoutEngineType: "flex" },
                { gridLayout: { cols: 12, rows: 12, cells: [{ x: 0, y: 0, width: 8, height: 6 }, { x: 8, y: 0, width: 4, height: 6 }, { x: 0, y: 6, width: 4, height: 6 }, { x: 4, y: 6, width: 4, height: 6 }, { x: 8, y: 6, width: 4, height: 6 }] }, fullBleed: false, showGutter: true, layoutEngineType: "flex" }
            ],
            6: [
                { gridLayout: { cols: 12, rows: 12, cells: [{ x: 0, y: 0, width: 4, height: 6 }, { x: 4, y: 0, width: 4, height: 6 }, { x: 8, y: 0, width: 4, height: 6 }, { x: 0, y: 6, width: 4, height: 6 }, { x: 4, y: 6, width: 4, height: 6 }, { x: 8, y: 6, width: 4, height: 6 }] }, fullBleed: false, showGutter: true, layoutEngineType: "flex" },
                { gridLayout: { cols: 12, rows: 12, cells: [{ x: 0, y: 0, width: 12, height: 8 }, { x: 0, y: 8, width: 3, height: 4 }, { x: 3, y: 8, width: 2, height: 4 }, { x: 5, y: 8, width: 2, height: 4 }, { x: 7, y: 8, width: 2, height: 4 }, { x: 9, y: 8, width: 3, height: 4 }] }, fullBleed: false, showGutter: true, layoutEngineType: "flex" },
                { gridLayout: { cols: 12, rows: 12, cells: [{ x: 0, y: 0, width: 9, height: 8 }, { x: 9, y: 0, width: 3, height: 4 }, { x: 0, y: 8, width: 3, height: 4 }, { x: 3, y: 8, width: 3, height: 4 }, { x: 6, y: 8, width: 3, height: 4 }, { x: 9, y: 4, width: 3, height: 8 }] }, fullBleed: true, showGutter: false, layoutEngineType: "flex" }
            ],
            7: [
                { gridLayout: { cols: 12, rows: 12, cells: [{ x: 0, y: 0, width: 3, height: 12 }, { x: 3, y: 0, width: 3, height: 6 }, { x: 6, y: 0, width: 3, height: 6 }, { x: 3, y: 6, width: 3, height: 6 }, { x: 6, y: 6, width: 3, height: 6 }, { x: 9, y: 0, width: 3, height: 6 }, { x: 9, y: 6, width: 3, height: 6 }] }, fullBleed: false, showGutter: true, layoutEngineType: "flex" }
            ],
            8: [
                { gridLayout: { cols: 12, rows: 12, cells: [{ x: 0, y: 0, width: 3, height: 6 }, { x: 3, y: 0, width: 3, height: 6 }, { x: 6, y: 0, width: 3, height: 6 }, { x: 9, y: 0, width: 3, height: 6 }, { x: 0, y: 6, width: 3, height: 6 }, { x: 3, y: 6, width: 3, height: 6 }, { x: 6, y: 6, width: 3, height: 6 }, { x: 9, y: 6, width: 3, height: 6 }] }, fullBleed: false, showGutter: true, layoutEngineType: "flex" }
            ],
            9: [
                { gridLayout: { cols: 12, rows: 12, cells: [{ x: 0, y: 0, width: 4, height: 4 }, { x: 4, y: 0, width: 4, height: 4 }, { x: 8, y: 0, width: 4, height: 4 }, { x: 0, y: 4, width: 4, height: 4 }, { x: 4, y: 4, width: 4, height: 4 }, { x: 8, y: 4, width: 4, height: 4 }, { x: 0, y: 8, width: 4, height: 4 }, { x: 4, y: 8, width: 4, height: 4 }, { x: 8, y: 8, width: 4, height: 4 }] }, fullBleed: false, showGutter: true, layoutEngineType: "flex" }
            ],
            10: [
                { gridLayout: { cols: 15, rows: 12, cells: [{ x: 0, y: 0, width: 3, height: 6 }, { x: 3, y: 0, width: 3, height: 6 }, { x: 6, y: 0, width: 3, height: 6 }, { x: 9, y: 0, width: 3, height: 6 }, { x: 12, y: 0, width: 3, height: 6 }, { x: 0, y: 6, width: 3, height: 6 }, { x: 3, y: 6, width: 3, height: 6 }, { x: 6, y: 6, width: 3, height: 6 }, { x: 9, y: 6, width: 3, height: 6 }, { x: 12, y: 6, width: 3, height: 6 }] }, fullBleed: false, showGutter: true, layoutEngineType: "flex" }
            ]
        };

        // Use 6-cell layout for 7, use 4-cell for missing counts, fallback to 1-cell for edge cases
        return layouts[count] || layouts[6] || layouts[4] || layouts[1];
    }

    buildPhotoCollageSlide(model, imageCount, layoutConfig) {
        let builder = new SlideModelBuilder("collage", model);

        // Only add header if there's a title
        if (model.slideTitle) {
            builder.createHeader({
                heading: model.slideTitle,
                position: HeaderPositionType.TOP
            });
        } else {
            // No header for photo-only slides
            builder.model.layout.headerPosition = HeaderPositionType.NONE;
        }

        // Get search terms - support both array format and legacy single search
        const searchTerms = this.getSearchTermsForImages(model, imageCount);

        const items = Array.from({ length: imageCount }, (_, index) => ({
            id: uuid(),
            componentType: "TextAndImage",
            childElement: {
                content_type: "image",
                enableFaceDetection: true,
                __generateImage: {
                    search: this.enhanceSearchForGrid(searchTerms[index].search),
                    searchStock: this.enhanceSearchForGrid(searchTerms[index].searchStock),
                    assetType: AssetType.IMAGE,
                    pickIndex: index
                }
            }
        }));

        builder.createPrimary({
            layoutEngineType: layoutConfig.layoutEngineType,
            gridLayout: layoutConfig.gridLayout,
            fullBleed: layoutConfig.fullBleed,
            showGutter: layoutConfig.showGutter,
            items
        });

        if (model.conclusion) {
            builder.createBottomTray(model.conclusion);
        }

        return builder.model;
    }

    buildPhotoTextListSlide(model) {
        let builder = new SlideModelBuilder("textgrid_photos", model);
        builder.createHeader();
        builder.createPrimary({
            items: model.items.map(item => ({
                ...builder.createImage(item.imageDescription, {}, false),
                text: {
                    blocks: [
                        builder.createBlock(item.title, TextStyleType.TITLE),
                        builder.createBlock(item.description, TextStyleType.BODY)
                    ].filter(b => b)
                }
            }))
        });

        builder.createBottomTray(model.conclusion);

        return builder.model;
    }

    getSearchTermsForImages(model, imageCount) {
        const searchTerms = [];

        // Check if we have items with imageSearchTerm
        if (model.items && model.items.length > 0) {
            // Use provided search terms
            for (let i = 0; i < imageCount; i++) {
                const item = model.items[i] || model.items[0];
                const searchTerm = item.imageSearchTerm || model.slideTitle || "abstract";
                searchTerms.push({
                    search: searchTerm,
                    searchStock: searchTerm
                });
            }
        } else {
            // Fallback: use single search term with variations
            const baseSearch = model.imageSearch || model.slideTitle || "abstract";
            const variations = [
                "",
                ", close up",
                ", wide angle",
                ", detail shot",
                ", aerial view",
                ", side view",
                ", front view",
                ", top view",
                ", dramatic lighting",
                ", natural light"
            ];

            for (let i = 0; i < imageCount; i++) {
                const variation = variations[i % variations.length];
                searchTerms.push({
                    search: baseSearch + variation,
                    searchStock: baseSearch + variation
                });
            }
        }

        return searchTerms;
    }

    enhanceSearchForGrid(searchTerm) {
        // Add composition hints to help ensure faces and important subjects are well-framed
        // These hints encourage centered compositions and full-frame subjects that work better in grid layouts
        if (!searchTerm) return searchTerm;

        return searchTerm + ", centered composition, well-framed subject";
    }
}
